import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, MenuProps, Popconfirm, Tooltip } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { useIntl } from 'umi';
import styles from './index.module.less';

export type ActionsMenuItem = {
  key: string;
  icon: React.ReactNode;
  label: string;
  danger?: boolean;
  disabled?: boolean;
  /** Why the item is disabled. */
  hint?: React.ReactNode;
  /** Asks first: for actions that reach beyond what's on screen or can't be undone. */
  confirm?: { title: React.ReactNode; content?: React.ReactNode };
  onSelect: () => void;
};

/** A titled group of items; sections are split by a divider. */
export type ActionsMenuSection = { key: string; title?: React.ReactNode; items: ActionsMenuItem[] };

type ActionsMenuProps = {
  items?: ActionsMenuItem[];
  sections?: ActionsMenuSection[];
  /** Opens an item's confirmation from elsewhere (e.g. a context menu), by key. */
  confirmKey?: string | null;
  onConfirmKeyChange?: (key: string | null) => void;
  className?: string;
};

/**
 * A ⋯ button with a menu of actions. An action that asks first does so in a
 * popconfirm on the ⋯ button. While the menu or the popconfirm is open,
 * Escape closes just that, not the drawer around it.
 */
const ActionsMenu: React.FC<ActionsMenuProps> = ({
  items,
  sections,
  confirmKey,
  onConfirmKeyChange,
  className,
}) => {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const [ownPendingKey, setOwnPendingKey] = useState<string | null>(null);
  const controlled = confirmKey !== undefined;
  const pendingKey = controlled ? confirmKey : ownPendingKey;
  const setPendingKey = (key: string | null) => {
    if (!controlled) setOwnPendingKey(key);
    onConfirmKeyChange?.(key);
  };
  const allSections: ActionsMenuSection[] = sections || [{ key: 'items', items: items || [] }];
  const allItems = allSections.flatMap((section) => section.items);
  const pending = allItems.find((item) => item.key === pendingKey && item.confirm && !item.disabled) || null;
  const setPending = (item: ActionsMenuItem | null) => setPendingKey(item?.key ?? null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const label = intl.formatMessage({ id: 'pages.tasks.moreActions', defaultMessage: 'More actions' });

  // Focus stays on the ⋯ button while confirming, so Escape lands in the handler below.
  useEffect(() => {
    if (pending) buttonRef.current?.focus();
  }, [pending?.key]);

  const select = (item: ActionsMenuItem) => {
    setMenuOpen(false);
    if (item.confirm) setPending(item);
    else item.onSelect();
  };

  const toMenuItem = (item: ActionsMenuItem) => ({
    key: item.key,
    icon: item.icon,
    danger: item.danger,
    disabled: item.disabled,
    label: item.disabled && item.hint ? <Tooltip title={item.hint}>{item.label}</Tooltip> : item.label,
    onClick: () => select(item),
  });
  const menuItems: MenuProps['items'] = allSections
    .filter((section) => section.items.length)
    .flatMap((section, index) => [
      ...(index ? [{ type: 'divider' as const, key: `${section.key}-divider` }] : []),
      section.title
        ? { type: 'group' as const, key: section.key, label: section.title, children: section.items.map(toMenuItem) }
        : section.items.map(toMenuItem),
    ])
    .flat();

  return (
    <span
      onKeyDown={(event) => {
        // The menu and the popconfirm close here, as their own listeners never hear the stopped key.
        if (event.key === 'Escape' && (menuOpen || pending)) {
          event.stopPropagation();
          setMenuOpen(false);
          setPending(null);
        }
      }}
    >
      <Popconfirm
        open={Boolean(pending)}
        onOpenChange={(open) => !open && setPending(null)}
        title={pending?.confirm?.title}
        description={
          pending?.confirm?.content ? <span className={styles.confirmText}>{pending.confirm.content}</span> : undefined
        }
        okText={pending?.label}
        okButtonProps={{ danger: pending?.danger }}
        cancelText={intl.formatMessage({ id: 'common.actions.cancel', defaultMessage: 'Cancel' })}
        placement="bottomRight"
        onConfirm={() => {
          pending?.onSelect();
          setPending(null);
        }}
      >
        <Dropdown
          trigger={['click']}
          placement="bottomRight"
          menu={{ items: menuItems }}
          open={menuOpen}
          onOpenChange={(open) => !pending && setMenuOpen(open)}
        >
          <Button
            ref={buttonRef}
            type="text"
            icon={<MoreOutlined />}
            aria-label={label}
            title={label}
            className={className || styles.actionsMenu}
          />
        </Dropdown>
      </Popconfirm>
    </span>
  );
};

export default ActionsMenu;
